import { trackWindowScroll } from "react-lazy-load-image-component";
import { ResultImageEntity } from "~/types/models/dashboard";
import { UiCenter, UiSimpleGrid, UiSkeletonText, UiText, UiWrap, useBreakpointValue } from "~/components/elements";
import { ImageThumbnail } from "~/pages/result-detail/components/ImageThumbnail";
import React, { useCallback, useState } from "react";
import ResultImageView from "~/components/modals/ResultImageView";
import { EChevronType } from "~/types/general";

export interface SharedImagesGalleryProps {
  images?: ResultImageEntity[];
  scrollPosition: any;
  loading?: boolean;
  emptyMessage?: string;
}

function SharedImagesGallery({ images, loading = false, emptyMessage = "", scrollPosition }: SharedImagesGalleryProps) {
  const Container = useBreakpointValue<any>({ base: UiSimpleGrid, sm: UiWrap });
  const [openModal, setOpenModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<ResultImageEntity>();
  const [imageNth, setImageNth] = useState(0);

  const handleOnClickChevron = useCallback(
    (chevronType: EChevronType) => {
      const currentImageIndex = images?.findIndex(o => o.id === selectedImage?.id);
      if (images && currentImageIndex !== undefined) {
        if (chevronType === EChevronType.Next && currentImageIndex < images.length - 1) {
          setSelectedImage(images[currentImageIndex + 1]);
          setImageNth(currentImageIndex + 2);
        }
        if (chevronType === EChevronType.Previous && currentImageIndex > 0) {
          setSelectedImage(images[currentImageIndex - 1]);
          setImageNth(currentImageIndex);
        }
      }
    },
    [images, selectedImage?.id]
  );

  const onClickImageThumbnail = useCallback((image: ResultImageEntity, idx: number) => {
    setSelectedImage(image);
    setImageNth(idx + 1);
    setOpenModal(true);
  }, []);

  return (
    <UiSkeletonText noOfLines={{ base: 3, md: 12 }} skeletonHeight={10} isLoaded={!loading} mt={4}>
      <ResultImageView
        imageSrc={selectedImage?.image_url}
        handleOnClickChevron={handleOnClickChevron}
        visible={openModal}
        totalItems={images?.length}
        itemNth={imageNth}
        onClose={() => setOpenModal(false)}
      />
      <Container w="full" spacing={4} minH="15rem" justify="center" columns={2}>
        {!images?.length && (
          <UiCenter
            minH="12rem"
            rounded="xl"
            borderWidth={1}
            mx="auto"
            px={10}
            bg="neutral.gray00"
            w="full"
            borderColor="red"
          >
            <UiText vr="title1" textAlign="center" color="red">
              {emptyMessage}
            </UiText>
          </UiCenter>
        )}
        {images?.map((image, idx) => (
          <ImageThumbnail
            key={image.id}
            imageId={image.id}
            scrollPosition={scrollPosition}
            idx={idx}
            favoriteable={false}
            votable
            maxSize={11.25}
            onClick={onClickImageThumbnail}
          />
        ))}
      </Container>
    </UiSkeletonText>
  );
}

export const TrackedSharedImagesGallery = trackWindowScroll(SharedImagesGallery);
